import { ACCESSIBILITY, GAME_FONT_FAMILY, PANEL_BG } from './uiStyles';
import {
    createKenneyButtonVisual,
    KenneyButtonKind,
    KenneyButtonState,
    resolveKenneyButtonTexture,
    setKenneyButtonState,
} from './kenneyButton';

export interface MenuCallbacks {
    onResume: () => void;
    onExit: () => void;
    onRestart: () => void;
    onCycleBackgroundTheme: () => string;
    getBackgroundThemeLabel: () => string;
}

type MenuButtonKind = KenneyButtonKind;
type MenuButtonState = KenneyButtonState;

interface MenuButton {
    key: string;
    background: Phaser.GameObjects.Image;
    label: Phaser.GameObjects.Text;
    hitArea: Phaser.GameObjects.Rectangle;
    kind: MenuButtonKind;
    state: MenuButtonState;
    onClick: () => void;
}

export class InGameMenu {
    private readonly scene: Phaser.Scene;
    private readonly callbacks: MenuCallbacks;

    private readonly root: Phaser.GameObjects.Container;
    private readonly backdrop: Phaser.GameObjects.Rectangle;
    private readonly panelBg: Phaser.GameObjects.Graphics;
    private readonly panelInner: Phaser.GameObjects.Graphics;
    private readonly panelBorder: Phaser.GameObjects.Graphics;
    private readonly divider: Phaser.GameObjects.Graphics;
    private readonly titleText: Phaser.GameObjects.Text;
    private readonly scoreText: Phaser.GameObjects.Text;

    private buttons: MenuButton[] = [];
    private menuMode: 'pause' | 'endgame' | null = null;
    private destroyed: boolean = false;

    public isMenuOpen: boolean = false;
    public isGameEnded: boolean = false;

    constructor(scene: Phaser.Scene, callbacks: MenuCallbacks) {
        this.scene = scene;
        this.callbacks = callbacks;

        this.root = this.scene.add.container(0, 0).setDepth(10000).setVisible(false);
        this.backdrop = this.scene.add.rectangle(0, 0, 1, 1, 0x0b1635, 0.78).setOrigin(0);
        this.backdrop.setInteractive();

        this.panelBg = this.scene.add.graphics();
        this.panelInner = this.scene.add.graphics();
        this.panelBorder = this.scene.add.graphics();
        this.divider = this.scene.add.graphics();

        this.titleText = this.scene.add.text(0, 0, 'PAUSED', {
            fontFamily: GAME_FONT_FAMILY,
            fontSize: '64px',
            color: '#ffffff',
            fontStyle: 'bold',
        }).setOrigin(0.5, 0);
        this.titleText.setStroke('#3559a5', 8);

        this.scoreText = this.scene.add.text(0, 0, '', {
            fontFamily: GAME_FONT_FAMILY,
            fontSize: '34px',
            color: '#ffffff',
            fontStyle: 'bold',
        }).setOrigin(0.5, 0.5);
        this.scoreText.setStroke('#000000', 6);
        this.scoreText.setVisible(false);

        this.root.add([
            this.backdrop,
            this.panelBg,
            this.panelInner,
            this.panelBorder,
            this.divider,
            this.titleText,
            this.scoreText,
        ]);
    }

    public togglePauseMenu(): void {
        if (this.destroyed) return;
        if (this.isGameEnded) return;

        this.isMenuOpen = !this.isMenuOpen;
        if (this.isMenuOpen) {
            this.showPauseMenu();
            return;
        }
        this.hideMenu();
    }

    public showEndGame(mainText: string, color: string, score?: number): void {
        if (this.destroyed) return;
        this.isGameEnded = true;
        this.isMenuOpen = true;
        this.menuMode = 'endgame';

        this.clearButtons();
        this.root.setVisible(true);

        this.titleText.setText(mainText);
        this.titleText.setStroke(color, 8);

        if (score !== undefined) {
            this.scoreText.setVisible(true);
            this.scoreText.setText(`SCORE: ${score}`);
        } else {
            this.scoreText.setVisible(false);
        }

        this.buttons = [
            this.createButton('restart', 'RESTART', 'green', () => this.callbacks.onRestart()),
            this.createButton('exit', 'EXIT', 'red', () => this.callbacks.onExit()),
        ];

        this.layout();
    }

    public hideMenu(): void {
        if (this.destroyed) return;
        this.isMenuOpen = false;
        this.root.setVisible(false);
        this.menuMode = null;
        this.clearButtons();
    }

    public resetState(): void {
        if (this.destroyed) return;
        this.hideMenu();
        this.isMenuOpen = false;
        this.isGameEnded = false;
        this.titleText.setStroke('#3559a5', 8);
        this.scoreText.setVisible(false);
    }

    public layout(): void {
        if (this.destroyed || !this.root.scene || !this.backdrop.scene) {
            return;
        }
        if (!this.root.visible && !this.isMenuOpen) {
            return;
        }

        const cam = this.scene.cameras.main;
        cam.preRender();
        const worldPerPixel = 1 / (cam.zoom || 1);
        const viewX = cam.worldView.x;
        const viewY = cam.worldView.y;
        const viewWidthPx = cam.width;
        const viewHeightPx = cam.height;
        const viewWidth = viewWidthPx * worldPerPixel;
        const viewHeight = viewHeightPx * worldPerPixel;

        this.root.setPosition(viewX, viewY);
        this.backdrop.setPosition(0, 0);
        this.backdrop.setSize(viewWidth, viewHeight);

        const isPortrait = viewHeightPx > viewWidthPx;
        const panelWidthPx = isPortrait ? Math.min(viewWidthPx * 0.86, 460) : Math.min(viewWidthPx * 0.6, 620);
        const panelHeightPx = this.menuMode === 'pause'
            ? (isPortrait ? Math.min(viewHeightPx * 0.74, 520) : Math.min(viewHeightPx * 0.8, 540))
            : (isPortrait ? Math.min(viewHeightPx * 0.72, 500) : Math.min(viewHeightPx * 0.74, 500));
        const panelWidth = panelWidthPx * worldPerPixel;
        const panelHeight = panelHeightPx * worldPerPixel;
        const panelX = (viewWidth - panelWidth) / 2;
        const panelY = (viewHeight - panelHeight) / 2;

        this.drawPanel(panelX, panelY, panelWidth, panelHeight);

        const titleSize = Math.round((isPortrait ? 42 : 56) * worldPerPixel);
        this.titleText.setFontSize(titleSize);
        this.titleText.setPosition(panelX + panelWidth / 2, panelY + 28 * worldPerPixel);

        const dividerY = panelY + 120 * worldPerPixel;
        this.drawDivider(panelX + 36 * worldPerPixel, dividerY, panelWidth - 72 * worldPerPixel, 6 * worldPerPixel);

        if (this.scoreText.visible) {
            this.scoreText.setFontSize(Math.round(28 * worldPerPixel));
            this.scoreText.setPosition(panelX + panelWidth / 2, panelY + 170 * worldPerPixel);
        }

        const buttonsTop = this.scoreText.visible ? panelY + 220 * worldPerPixel : panelY + 165 * worldPerPixel;
        const minButtonHeight = ACCESSIBILITY.minTouchTargetPx * worldPerPixel;
        const buttonHeight = Math.max(minButtonHeight, Math.round((isPortrait ? 58 : 62) * worldPerPixel));
        const buttonWidth = panelWidth - 90 * worldPerPixel;
        const minButtonGap = 10 * worldPerPixel;
        const buttonGap = Math.max(minButtonGap, Math.round(16 * worldPerPixel));

        this.buttons.forEach((button, idx) => {
            const x = panelX + (panelWidth - buttonWidth) / 2;
            const y = buttonsTop + idx * (buttonHeight + buttonGap);
            this.layoutButton(button, x, y, buttonWidth, buttonHeight);
        });
    }

    public destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.clearButtons();
        this.root.destroy(true);
    }

    private showPauseMenu(): void {
        this.menuMode = 'pause';
        this.root.setVisible(true);
        this.clearButtons();

        this.titleText.setText('PAUSED');
        this.titleText.setStroke('#3559a5', 8);
        this.scoreText.setVisible(false);

        const themeLabel = `THEME: ${this.callbacks.getBackgroundThemeLabel()}`;

        this.buttons = [
            this.createButton('resume', 'RESUME', 'blue', () => this.callbacks.onResume()),
            this.createButton('theme', themeLabel, 'blue', () => {
                const nextThemeLabel = this.callbacks.onCycleBackgroundTheme();
                const target = this.buttons.find((button) => button.key === 'theme');
                if (target) {
                    target.label.setText(`THEME: ${nextThemeLabel}`);
                    this.layout();
                }
            }),
            this.createButton('exit', 'EXIT GAME', 'red', () => this.callbacks.onExit()),
        ];

        this.layout();
    }

    private createButton(key: string, text: string, kind: MenuButtonKind, onClick: () => void): MenuButton {
        const visual = createKenneyButtonVisual({
            scene: this.scene,
            text,
            kind,
            originX: 0,
            originY: 0,
            labelStyle: {
                fontSize: '30px',
            },
        });

        const background = visual.background;
        const label = visual.label;
        const hitArea = visual.hitArea;

        hitArea.on('pointerdown', () => {
            this.setButtonState(button, 'pressed');
        });
        hitArea.on('pointerover', () => {
            if (button.state !== 'pressed') this.setButtonState(button, 'hover');
        });
        hitArea.on('pointerup', () => {
            this.setButtonState(button, 'hover');
            onClick();
        });
        hitArea.on('pointerout', () => {
            this.setButtonState(button, 'normal');
        });

        const button: MenuButton = { key, background, label, hitArea, kind, state: 'normal', onClick };
        this.root.add([background, hitArea, label]);
        return button;
    }

    private layoutButton(button: MenuButton, x: number, y: number, width: number, height: number): void {
        const worldPerPixel = 1 / (this.scene.cameras.main.zoom || 1);
        button.hitArea.setPosition(x, y);
        button.hitArea.setSize(width, height);

        button.background.setPosition(x, y);
        button.background.setDisplaySize(width, height);

        const labelYOffset = button.state === 'pressed' ? Math.max(1, Math.round(height * 0.04)) : 0;
        button.label.setPosition(x + width / 2, y + height / 2 + labelYOffset);
        button.label.setFontSize(Math.round(Math.max(20 * worldPerPixel, height * 0.45)));
        const useEndGameContrastLabel = this.menuMode === 'endgame' && (button.key === 'restart' || button.key === 'exit');
        if (useEndGameContrastLabel) {
            button.label.setColor('#ffffff');
            button.label.setStroke('#000000', 4);
        } else {
            button.label.setColor(button.kind === 'green' ? '#0f3c2f' : '#ffffff');
            button.label.setStroke(button.kind === 'green' ? '#f8ffe8' : '#163670', 4);
        }

        button.background.setTexture(resolveKenneyButtonTexture(button.kind, button.state));
    }

    private setButtonState(button: MenuButton, state: MenuButtonState): void {
        setKenneyButtonState(button, state, 0.985);
        this.layout();
    }

    private drawPanel(x: number, y: number, width: number, height: number): void {
        this.panelBg.clear();
        this.panelInner.clear();
        this.panelBorder.clear();

        this.panelBg.fillStyle(PANEL_BG.fillColor, PANEL_BG.fillAlpha);
        this.panelBg.fillRoundedRect(x, y, width, height, 10);

        this.panelBorder.lineStyle(PANEL_BG.lineWidth, PANEL_BG.strokeColor, PANEL_BG.strokeAlpha);
        this.panelBorder.strokeRoundedRect(x, y, Math.max(1, width), Math.max(1, height), 10);
    }

    private drawDivider(x: number, y: number, width: number, height: number): void {
        this.divider.clear();
        this.divider.fillStyle(PANEL_BG.strokeColor, 0.22);
        this.divider.fillRoundedRect(x, y, width, height, 2);
    }

    private clearButtons(): void {
        if (this.destroyed) {
            this.buttons = [];
            return;
        }
        this.buttons.forEach((button) => {
            button.background.destroy();
            button.hitArea.destroy();
            button.label.destroy();
        });
        this.buttons = [];
    }
}
